/**
 * `markdown-parser` is a web component that parses and sanitizes markdown content from slotted content, allowing only specific HTML tags based on an attribute.
 *
 * @property {String} allowedTags - A comma-separated list of allowed HTML tags (e.g., "strong, em").
 */

import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import markdownIt from 'markdown-it';
import markdownItForInlineIterator from 'markdown-it-for-inline';

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Defines a custom element `<uo-markdown>` using the LitElement base class. This web component parses markdown content into sanitized HTML, allowing only a specified set of HTML tags. It uses the `markdown-it` library to convert markdown to HTML and applies filtering based on the `allowed-tags` property.
 */
@customElement( 'uo-markdown' )
class AutomatorMarkdownParser extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties

	/**
	 * A comma-separated string of allowed HTML tags.
	 * Example: "strong,em"
	 * @type {String}
	 */
	@property( { type: String, attribute: 'allowed-tags' } ) accessor allowedTags = 'strong,em';

	/**
	 * The content of the markdown to parse.
	 * @type {String}
	 */
	@property( { type: String } ) accessor content;

	// Initialize markdown-it with HTML disabled
	md = markdownIt( {
		// Disable HTML tags in source
		html: false,
	} )
		// Add target="_blank" to all links
		.use(
			// Plugin "markdown-it-for-inline"
			markdownItForInlineIterator,
			// Rule name
			'url_new_win',
			// Type of token to look for in inline tokens
			'link_open',
			/**
			 * Iterator function
			 *
			 * @param {Array} tokens - The tokens array
			 * @param {Number} idx - The index of the token to modify
			 */
			( tokens, idx ) => {
				tokens[ idx ].attrSet( 'target', '_blank' );
			}
		);

	/**
	 * The render function renders the slot, and then the updated lifecycle method will handle the rest.
	 *
	 * @return {TemplateResult} The slotted content.
	 */
	render() {
		return this._templateParsedContent;
	}

	/**
	 * The template for the parsed content.
	 *
	 * @return {TemplateResult|undefined} The parsed content.
	 */
	get _templateParsedContent() {
		// If there is no content, return nothing
		if ( ! this.content ) {
			return;
		}

		// Convert the entire text content to HTML using markdown-it
		let parsedHTML = this.convertMarkdownToHTML( this.content );

		// Check if we have to filter the HTML based on allowedTags
		if ( this.allowedTags ) {
			// Filter the HTML based on allowedTags
			parsedHTML = this.filterHTMLTags( parsedHTML );
		}

		// The "unsafe HTML" here is the HTML created by markdown-it
		// The code that markdown-it is highlighting is escaped, so it won't be executed
		return html`${ unsafeHTML( parsedHTML ) }`;
	}

	/**
	 * Converts the extracted plain text to HTML using the markdown-it library.
	 *
	 * @param {String} content - The plain text content to convert.
	 * @return {String} The HTML generated from the markdown.
	 */
	convertMarkdownToHTML( content ) {
		return this.md.render( content );
	}

	/**
	 * Filters the HTML based on allowed tags, removing any tags not in the allowedTags list.
	 *
	 * @param {String} htmlContent - The HTML content to filter.
	 * @return {String} The sanitized HTML.
	 */
	filterHTMLTags( htmlContent ) {
		const parser = new DOMParser();
		const doc = parser.parseFromString( htmlContent, 'text/html' );
		const allowedTagsArray = this.allowedTags.split( ',' ).map( tag => tag.trim() );

		this.recursivelyFilterTags( doc.body, allowedTagsArray );

		return doc.body.innerHTML;
	}

	/**
	 * Recursively filters the child elements of a given DOM element, removing any tags that are not in the allowedTags array.
	 *
	 * @param {HTMLElement} element - The root element to filter.
	 * @param {String[]} allowedTags - An array of allowed tag names.
	 */
	recursivelyFilterTags( element, allowedTags ) {
		// Process all child nodes first
		Array.from( element.childNodes ).forEach( child => {
			if ( child.nodeType === Node.ELEMENT_NODE ) {
				// Recursively filter the child’s own children
				this.recursivelyFilterTags( child, allowedTags );
			}
		} );

		// Now, filter this element’s immediate children in reverse order
		// (Reverse order is useful when modifying the DOM while iterating)
		const children = Array.from( element.childNodes ).reverse();
		children.forEach( child => {
			if ( child.nodeType !== Node.ELEMENT_NODE ) {
				return;
			}

			// If the tag is not allowed, replace it with its (now filtered) children
			if ( ! allowedTags.includes( child.tagName.toLowerCase() ) ) {
				child.replaceWith( ...Array.from( child.childNodes ) );
			}
		} );
	}
}
